import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';
import { CtaBand, HeroLead, PageHero } from '@/components/ui';
import { COMPANY, SITE } from '@/content/company';
import { ENGAGEMENT_MODELS, FAQ } from '@/content/services';
import styles from './Company.module.css';

export function Company() {
  return (
    <>
      <PageHero
        eyebrow="Company"
        title="A small engineering company, registered in Bratislava."
        lead={
          <HeroLead>
            {COMPANY.legalName} is a Slovak limited liability company building eCommerce and custom
            software for clients across Europe.
          </HeroLead>
        }
      />

      <div className={styles.intro}>
        <section aria-labelledby="setup-heading">
          <h2 id="setup-heading" className={styles.columnTitle}>
            How we are set up
          </h2>
          <p className={styles.prose}>
            We are deliberately small. Every project is staffed by people who write code, and the
            person you speak with in the first conversation stays involved through delivery.
          </p>
          <p className={styles.prose}>
            That shape suits the work we take on: focused engagements with clear ownership, where
            the value comes from understanding the business properly rather than from adding
            headcount.
          </p>
          <p className={styles.prose}>
            We work with growing merchants who need a technical partner, and with agencies and
            larger organisations who need a specialist team alongside their own.
          </p>
        </section>

        <section aria-labelledby="engage-heading">
          <h2 id="engage-heading" className={styles.columnTitle}>
            How we engage
          </h2>
          <dl className={styles.engagements}>
            {ENGAGEMENT_MODELS.map((model) => (
              <div className={styles.engagement} key={model.name}>
                <dt className={styles.engagementName}>{model.name}</dt>
                <dd className={styles.engagementDetail}>{model.detail}</dd>
              </div>
            ))}
          </dl>
          <p className={styles.engagementNote}>
            Work is quoted individually and invoiced in EUR. Contracts, invoicing and VAT handling
            follow standard Slovak and EU practice, including reverse charge for VAT-registered
            clients in other member states. We can supply the documentation partners and payment
            providers typically ask for.
          </p>
        </section>
      </div>

      <LegalDetails />

      <Faq />

      <CtaBand
        title="Working with us"
        text="Send a short description of the project and we will reply, usually within one working day."
      />
    </>
  );
}

/**
 * Frequently asked questions.
 *
 * Real `<details>` elements rather than a JavaScript accordion: they are
 * keyboard operable, announce their own expanded state, and are searchable by
 * the browser's find-in-page even while collapsed. The first is open so the
 * section does not read as an empty stack of bars.
 *
 * The answers are also emitted as `FAQPage` structured data - see
 * content/structured-data.ts - from this same array, so the markup cannot drift
 * away from what the page says.
 */
function Faq() {
  return (
    <section className={styles.faq} aria-labelledby="faq-heading">
      <div className={styles.faqHead}>
        <h2 id="faq-heading" className={styles.columnTitle}>
          Common questions
        </h2>
        <p className={styles.faqLead}>{SITE.factualSummary}</p>
      </div>

      <div className={styles.faqList}>
        {FAQ.map((item, index) => (
          <details className={styles.faqItem} key={item.question} open={index === 0}>
            <summary className={styles.faqQuestion}>{item.question}</summary>
            <p className={styles.faqAnswer}>{item.answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function LegalDetails() {
  const facts: { term: string; value: ReactNode; mono?: boolean }[] = [
    { term: 'Legal name', value: COMPANY.legalName },
    { term: 'IČO', value: COMPANY.ico, mono: true },
    { term: 'Trading name', value: COMPANY.tradingName },
    { term: 'DIČ', value: COMPANY.dic, mono: true },
    { term: 'Legal form', value: COMPANY.legalForm },
    { term: 'IČ DPH', value: COMPANY.icDph, mono: true },
    { term: 'Incorporated', value: COMPANY.incorporated },
    { term: 'VAT status', value: COMPANY.vatStatus },
  ];

  return (
    <section className={styles.legal} aria-labelledby="legal-heading">
      <div className={styles.legalBox}>
        <div className={styles.legalHead}>
          <h2 id="legal-heading" className={styles.legalHeadTitle}>
            Legal &amp; registration details
          </h2>
          <span className={styles.legalHeadNote}>Registered in the Slovak Republic</span>
        </div>

        {/* dt/dd go straight into the grid - a `display: contents` wrapper is
            an extra box the layout engine still rounds against, and it cost a
            pixel of row height against the design. */}
        <dl className={styles.factGrid}>
          {facts.map((fact) => (
            <Fragment key={fact.term}>
              <dt>{fact.term}</dt>
              <dd className={fact.mono ? styles.factMono : undefined}>{fact.value}</dd>
            </Fragment>
          ))}
        </dl>

        <div className={styles.detailGrid}>
          <div className={styles.detail}>
            <h3 className={styles.detailTerm}>Commercial register</h3>
            <p className={styles.detailValue}>
              {COMPANY.register.court}
              <br />
              {COMPANY.register.section}
            </p>
          </div>

          <div className={styles.detail}>
            <h3 className={styles.detailTerm}>Registered address</h3>
            <address className={styles.detailValue}>
              {COMPANY.address.street}
              <br />
              {COMPANY.address.localityFormal}
              <br />
              {COMPANY.address.country}
            </address>
          </div>

          <div className={styles.detail}>
            <h3 className={styles.detailTerm}>Statutory body</h3>
            <p className={styles.detailValue}>
              {COMPANY.statutory.name}, {COMPANY.statutory.role}
              <br />
              <span className={styles.detailMuted}>{COMPANY.statutory.note}</span>
            </p>
          </div>

          {/*
           * Email, and a pointer to the phone rather than the phone itself. The
           * number is fetched from the backend after a captcha check, and that
           * control may only live on /contact - it is the one route whose CSP
           * permits Google, and the one page carrying the required reCAPTCHA
           * attribution. See src/components/PhoneReveal.tsx.
           */}
          <div className={styles.detail}>
            <h3 className={styles.detailTerm}>Contact</h3>
            <p className={`${styles.detailValue} sk-prose-link`}>
              <a href={`mailto:${COMPANY.email}`} className="sk-hit">
                {COMPANY.email}
              </a>
              <br />
              {/*
               * A sentence, not a bare "Phone number" link. The label used to
               * promise the thing it could not deliver: pressing it navigated to
               * another page, where a captcha button still stood between the
               * visitor and the number. Naming the destination instead of the
               * payload matches Terms §2 and the Accessibility statement, which
               * point at the same page in the same words.
               *
               * No .sk-hit here, unlike the email link above it. The link is now
               * inside a sentence, so WCAG 2.2 SC 2.5.8 exempts it under the
               * Inline exception and padding it would open a gap in the line.
               */}
              <span className={styles.detailMuted}>
                Phone number is on our <Link to="/contact">contact page</Link>.
              </span>
            </p>
          </div>

          {/* Required by § 4(1) of Act 22/2004 Coll. on electronic commerce -
              see the note on COMPANY.supervision. Added in pairs because the
              grid draws its dividers from nth-child parity; an odd number of
              cards leaves a border hanging on the last row. */}
          <div className={styles.detail}>
            <h3 className={styles.detailTerm}>Supervisory authority</h3>
            <address className={styles.detailValue}>
              {COMPANY.supervision.authority}
              <br />
              {COMPANY.supervision.office}
              <br />
              <span className={styles.detailMuted}>{COMPANY.supervision.address}</span>
            </address>
          </div>

          <div className={styles.detail}>
            <h3 className={styles.detailTerm}>Data protection authority</h3>
            <address className={styles.detailValue}>
              {COMPANY.dataProtectionAuthority.name}
              <br />
              <span className={styles.detailMuted}>{COMPANY.dataProtectionAuthority.address}</span>
            </address>
          </div>
        </div>
      </div>
    </section>
  );
}
